import {
  PRE_CHART_LAB_TESTS_MAX,
  type PreChartLabMode,
  type PreChartLabs,
  preChartLabsSchema,
} from "@rosc/contracts";
import { useSyncExternalStore } from "react";

export type { PreChartLabMode, PreChartLabs };

// "Pre-chart labs" (clinician feedback 03/11): which lab values a pre-chart
// takes from documents. The clinician's setting applies to every pre-chart
// and one pre-chart can override it. Both are remembered in this browser,
// per account, like the template favourites (the account has no server-side
// field for it yet; that would need a database migration). The choice is
// sent with each assistant edit and only used on a pre-chart draft.

export const PRE_CHART_LABS_DEFAULT: PreChartLabs = { mode: "all" };

type Account = string | null | undefined;
const ACCOUNT_PREFIX = "rosc:preChartLabs:";
const VISIT_PREFIX = "rosc:preChartLabs:visit:";

const get = (key: string): string | null => {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
};
const put = (key: string, value: string | null): void => {
  try {
    if (value === null) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, value);
  } catch {
    // Storage unavailable (private mode): the choice lasts for this page.
  }
};

const listeners = new Set<() => void>();
let version = 0;
const emit = (): void => {
  version++;
  for (const listener of listeners) listener();
};
const onStorage = (event: StorageEvent): void => {
  if (event.key === null || event.key.startsWith(ACCOUNT_PREFIX)) emit();
};
const subscribe = (listener: () => void): (() => void) => {
  if (listeners.size === 0) window.addEventListener("storage", onStorage);
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) window.removeEventListener("storage", onStorage);
  };
};
// Re-renders when a setting changes (here or in another tab).
export const usePreChartLabsVersion = (): number =>
  useSyncExternalStore(
    subscribe,
    () => version,
    () => version,
  );

const parse = (raw: string | null): PreChartLabs | null => {
  if (!raw) return null;
  try {
    const parsed = preChartLabsSchema.safeParse(JSON.parse(raw));
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
};

const accountKey = (account: Account): string =>
  `${ACCOUNT_PREFIX}${account ?? "local"}`;
const visitKey = (account: Account, visitPublicId: string): string =>
  `${VISIT_PREFIX}${account ?? "local"}:${visitPublicId}`;

// The test list as the clinician types it: one name per comma, semicolon
// or line (the same rule as the server's parseLabTestList).
export const parseTestList = (text: string): string[] => {
  const seen = new Set<string>();
  const tests: string[] = [];
  for (const part of text.split(/[,;\n]+/)) {
    const name = part.trim().replace(/\s+/g, " ").slice(0, 60);
    const key = name.toLowerCase();
    if (!name || seen.has(key)) continue;
    seen.add(key);
    tests.push(name);
  }
  return tests.slice(0, PRE_CHART_LAB_TESTS_MAX);
};

// A setting as saved: "Only these tests" without a test is "All values".
export const normalizeLabs = (value: PreChartLabs): PreChartLabs =>
  value.mode === "tests"
    ? value.tests?.length
      ? { mode: "tests", tests: [...value.tests] }
      : PRE_CHART_LABS_DEFAULT
    : { mode: value.mode };

export const readAccountLabs = (account: Account): PreChartLabs =>
  parse(get(accountKey(account))) ?? PRE_CHART_LABS_DEFAULT;

export const writeAccountLabs = (
  account: Account,
  value: PreChartLabs,
): void => {
  const next = normalizeLabs(value);
  put(accountKey(account), next.mode === "all" ? null : JSON.stringify(next));
  emit();
};

// This pre-chart's own choice, or null when it follows the setting.
export const readVisitLabs = (
  account: Account,
  visitPublicId: string,
): PreChartLabs | null => parse(get(visitKey(account, visitPublicId)));

export const writeVisitLabs = (
  account: Account,
  visitPublicId: string,
  value: PreChartLabs | null,
): void => {
  put(
    visitKey(account, visitPublicId),
    value ? JSON.stringify(normalizeLabs(value)) : null,
  );
  emit();
};

// What an edit of this visit sends: the pre-chart's own choice, else the
// setting; nothing for "All values".
export const preChartLabsFor = (
  account: Account,
  visitPublicId: string,
): PreChartLabs | undefined => {
  const value =
    readVisitLabs(account, visitPublicId) ?? readAccountLabs(account);
  return value.mode === "all" ? undefined : value;
};
